import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import clsx from "clsx";
import { goBack } from "connected-react-router";

import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import AppBar from "@material-ui/core/AppBar";
import MenuIcon from "@material-ui/icons/Menu";
import Button from "@material-ui/core/Button";
import { useMediaQuery } from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";

import routes from "common/routes";
import Breakpoints from "common/breakpoints";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { matchRoute } from "utils/routes";
import { setStallApplicationUnsavedChangesPopupState } from "store/actions/racingSecretaryStallApplication";
import BackLink from "components/BackLink";
import {
  useClickBlockerContext,
  withClickBlocker,
  WithClickBlockerProps
} from "components/BlockableClickContext";
import AppSideBar from "components/AppSideBar";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import AccountPageSlider from "../AccountPageSlider/AccountPageSlider";

import useStyles from "./styles";

export interface Props extends WithClickBlockerProps {
  className?: string;
  isProfilePage?: boolean;
  pathname?: string;
}

const AppHeader = (props: Props) => {
  const { className, handleBlockableLinkClick } = props;

  const classes = useStyles();
  const { currentUser } = useLoggedInUser();
  const location = useLocation();
  const dispatch = useDispatch();
  const { blockClickCallback: handleOpenVetExamModal } =
    useClickBlockerContext();
  const matchesDownSM600 = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(Breakpoints.SM_600)
  );

  const [menuOpen, setMenuOpen] = useState(false);

  const swipeAreaWidth = matchesDownSM600 ? 16 : 0;
  const {
    activeLink,
    isExamFormOpen,
    isEditPastExamFormOpen,
    isOpenPreferredConditions,
    isOpenMapOnTrainerHorseDetails,
    isOpenMapOnVetHorseDetails,
    shouldBackArrowBeShownByCustomReason,
    isRacingSecretaryStallApplicationDetailsOpen
  } = useAppSelector(state => {
    const {
      sidebar,
      trainerHorseDetailsPage,
      vetHorseDetailsPage,
      vetExam,
      racingSecretaryStallApplication
    } = state;

    return {
      activeLink: sidebar.activeLink,
      shouldBackArrowBeShownByCustomReason: sidebar.shouldBackArrowBeShown,
      isOpenPreferredConditions:
        trainerHorseDetailsPage.preferredConditionsState,
      isOpenMapOnTrainerHorseDetails: trainerHorseDetailsPage.mapPopupState,
      isExamFormOpen: vetExam.isExamFormOpen,
      isEditPastExamFormOpen: vetExam.isEditPastExamFormOpen,
      isOpenMapOnVetHorseDetails: vetHorseDetailsPage.mapPopupState,
      isRacingSecretaryStallApplicationDetailsOpen:
        racingSecretaryStallApplication.stallApplicationDetailsState
    };
  }, shallowEqual);

  const hasOpenPopupOnHorseDetails = [
    isExamFormOpen,
    isOpenPreferredConditions,
    isOpenMapOnTrainerHorseDetails,
    isOpenMapOnVetHorseDetails
  ].some(item => item);

  const showBackButton =
    (!hasOpenPopupOnHorseDetails &&
      !(
        matchRoute(
          location.pathname,
          [
            { path: activeLink },
            routes.workoutRequests,
            routes.workoutExams,
            routes.workoutPassed,
            routes.workoutFailed,
            routes.workoutRejected,
            routes.preRaceScreeningEntries,
            routes.preRaceScreeningEntryExams,
            routes.preRaceScreeningResults,
            routes.preRaceScreeningRaceDay
          ],
          true
        ) || location.pathname === "/"
      )) ||
    location.pathname === routes.account.path ||
    shouldBackArrowBeShownByCustomReason;

  const handleCancelClick = e => {
    if (!handleBlockableLinkClick(e)) {
      dispatch(goBack());
    }
  };

  const handleCloseRacingSecretaryStallApplication = () => {
    dispatch(setStallApplicationUnsavedChangesPopupState(true));
  };

  const getTitle = (): string => {
    let title = "";

    for (const route in routes) {
      if (matchRoute(location.pathname, [routes[route]])) {
        title = routes[route].moduleName;
      }
    }

    return title;
  };

  const toggleHamburgerMenu =
    (isOpen: boolean) => (event: React.MouseEvent) => {
      setMenuOpen(isOpen);
      event && event.preventDefault();
    };

  const renderHeaderLeftSide = () => {
    if (isExamFormOpen || isEditPastExamFormOpen) {
      return (
        <Button
          className={classes.closeVetExamPage}
          onClick={handleOpenVetExamModal}
        >
          Close
        </Button>
      );
    }

    if (isRacingSecretaryStallApplicationDetailsOpen) {
      return (
        <Button
          className={classes.cancelButton}
          onClick={handleCloseRacingSecretaryStallApplication}
        >
          Cancel
        </Button>
      );
    }

    if (
      matchRoute(location.pathname, [
        currentUser.isRacingOfficial
          ? routes.stallApplicationNewForm
          : routes.stallApplicationTrainerForm
      ])
    ) {
      return (
        <Button className={classes.cancelButton} onClick={handleCancelClick}>
          Cancel
        </Button>
      );
    }

    if (showBackButton) {
      return <BackLink className={classes.logoBackIcon} />;
    }

    return (
      <IconButton
        className={classes.hamburgerButton}
        onClick={toggleHamburgerMenu(true)}
      >
        <MenuIcon className={classes.hamburgerIcon} />
      </IconButton>
    );
  };

  useEffect(() => {
    if (!matchesDownSM600) {
      setMenuOpen(false);
    }
  }, [matchesDownSM600]);

  return (
    <div className={classes.appHeader}>
      <section
        className={clsx(className, { [classes.hasBackButton]: showBackButton })}
      >
        <AppBar position="static" className={classes.appBar} elevation={0}>
          <Toolbar className={classes.toolbar} disableGutters={true}>
            <div className={classes.header}>
              <div className={classes.headerLeftSideBlock}>
                {renderHeaderLeftSide()}
              </div>

              <Typography
                component="h1"
                variant="h6"
                color="inherit"
                noWrap
                className={classes.title}
              >
                {getTitle()}
              </Typography>

              <div className={classes.headerRightSideBlock}>
                <AccountPageSlider />
              </div>
            </div>
          </Toolbar>
        </AppBar>
      </section>
      <SwipeableDrawer
        swipeAreaWidth={swipeAreaWidth}
        open={menuOpen}
        onClose={toggleHamburgerMenu(false)}
        onOpen={toggleHamburgerMenu(true)}
        anchor="left"
      >
        <AppSideBar
          className={classes.appSideBar}
          onLinkClick={toggleHamburgerMenu(false)}
          isSwipeableDrawer={menuOpen}
        />
      </SwipeableDrawer>
    </div>
  );
};

export default withClickBlocker(AppHeader);
